import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventoryUsageService from '../../services/InventoryUsageService.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import { FileText } from "lucide-react";
import { useTranslation } from 'react-i18next';


const EditInventoryUsage = () => {
  const { usageId } = useParams();
  const [formData, setFormData] = useState({
    usage_type: '',
    usage_purpose: '',
    used_by_name: '',
    department: '',
    usage_date: '',
    description: ''
  });
  
  const [returnReceipt, setReturnReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const navigate = useNavigate();

  const usageTypes = inventoryUsageService.getUsageTypes();
  const usagePurposes = inventoryUsageService.getUsagePurposes();

  useEffect(() => {
    fetchUsageRecord();
  }, [usageId]);

  const fetchUsageRecord = async () => {
    try {
      const response = await inventoryUsageService.getById(usageId);
      const data = response.data;
      setUsageData(data);
      setFormData({
        usage_type: data.usage_type || '',
        usage_purpose: data.usage_purpose || '',
        used_by_name: data.used_by_name || '',
        department: data.department || '',
        usage_date: data.usage_date || '',
        description: data.description || ''
      });
    } catch (err) {
      Alert.error('Failed to load usage record');
      console.error(err);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt must be less than 5MB');
        return;
      }
      setReturnReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReturnReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await inventoryUsageService.update(usageId, formData, returnReceipt);
      Alert.success('Usage record updated successfully!');
      setTimeout(() => navigate(`/inventory-usage/view/${usageId}`), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update usage record');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading usage record...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!usageData) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <FileText className="w-5 h-5 text-buttonBg" /> Edit Usage Record
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Item Info (Read-only) */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Item Information</h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                          <span className="text-xs text-gray-600">Usage ID</span>
                          <p className="text-sm font-semibold text-gray-900">{usageData.usage_id}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">Item Name</span>
                          <p className="text-sm font-semibold text-gray-900">{usageData.item_name}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">Quantity Used</span>
                          <p className="text-sm font-semibold text-gray-900">{usageData.quantity_used} {usageData.unit}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">Total Cost</span>
                          <p className="text-sm font-semibold text-green-600">₹{usageData.total_cost}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Usage Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Usage Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Type <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="usage_type"
                          value={formData.usage_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Usage Type</option>
                          {usageTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Purpose <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="usage_purpose"
                          value={formData.usage_purpose}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Purpose</option>
                          {usagePurposes.map((purpose) => (
                            <option key={purpose.value} value={purpose.value}>
                              {purpose.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="usage_date"
                          value={formData.usage_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: User Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">User Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Used By Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="used_by_name"
                          value={formData.used_by_name}
                          onChange={handleChange}
                          placeholder="Enter name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Department
                        </label>
                        <input
                          type="text"
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          placeholder="Enter department"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Description & Receipt */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Description & Receipt</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter usage description"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Return Receipt / Proof
                        </label>
                        <input
                          id="receipt-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleReceiptChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload new receipt to replace existing one (PDF or Image, Max 5MB)</p>
                        {receiptPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={receiptPreview} 
                                alt="Receipt Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeReceipt}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove receipt"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Updating...' : 'Update Record'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/inventory-usage/view/${usageId}`)}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInventoryUsage;